/* Analyzing screen — polls the scan until it settles, and paces the step list so the
   progress is actually watchable.

   The real pipeline is CPU-fast: without pacing the server reports "step 5 of 5" on the
   first poll and the user sees a finished list with no motion. So the ticks advance on a
   timer (like the prototype) but never run ahead of the server: if the server is slower —
   a SERP run, a long AI pass — the current step just keeps spinning until it catches up. */

import { api } from "../api.js";

const FALLBACK_STEPS = [
  "Reading your Search performance",
  "Reading your Generative AI report",
  "Grouping searches into topics",
  "Matching topics to your pages",
  "Scoring your AI opportunities",
];

const TICK_MS = 420;      // one step ticks off at most this often (prototype timing)
const FINISH_MS = 350;    // pause on the completed list before moving on

export function startAnalyzing(container, { scanId, onCompleted, onFailed, onCancel }) {
  container.innerHTML = `
    <div style="min-height:100vh;background:var(--bg);display:flex;align-items:center;justify-content:center;font-family:var(--font)">
      <div style="background:var(--surface);border:1px solid var(--line);border-radius:16px;width:380px;padding:28px">
        <div style="font-weight:800;font-size:16px;margin-bottom:16px">Analyzing your data</div>
        <div id="step-list"></div>
        <div id="analyzing-note" style="display:none;font-size:11.5px;color:var(--muted);line-height:1.5;margin-top:2px"></div>
        <div id="analyzing-error" style="display:none;margin-top:12px;font-size:12px;color:var(--red);background:var(--red-soft);border-radius:10px;padding:10px 12px;line-height:1.5"></div>
        <button id="btn-cancel" style="border:none;background:transparent;color:var(--muted);font-family:var(--font);font-size:12px;font-weight:600;cursor:pointer;padding:14px 0 0">← Back to dashboard</button>
      </div>
    </div>
    <style>
      @keyframes agf-spin { to { transform: rotate(360deg) } }
      @keyframes agf-pop { from { transform: scale(.5); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      .agf-step { display:flex; align-items:center; gap:10px; margin-bottom:10px; transition: opacity .35s ease, transform .35s ease }
      .agf-step .agf-label { font-size:13px; transition: color .35s ease, font-weight .2s ease }
      .agf-mark { width:14px; height:14px; flex-shrink:0; display:inline-block }
      .agf-mark.done { animation: agf-pop .25s ease-out }
      .agf-mark.active { border-radius:50%; border:2px solid var(--line); border-top-color: var(--violet); animation: agf-spin .7s linear infinite }
      .agf-mark.waiting { border-radius:50%; border:2px solid var(--line) }
    </style>`;

  const list = container.querySelector("#step-list");
  const note = container.querySelector("#analyzing-note");
  const errBox = container.querySelector("#analyzing-error");
  container.querySelector("#btn-cancel").addEventListener("click", () => { stopped = true; onCancel(); });

  const CHECK = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

  let steps = FALLBACK_STEPS.slice();
  let rows = [];
  let ticked = 0;          // how many steps are shown as done
  let serverDone = 0;      // how many the server says are finished
  let settledScan = null;  // the completed scan, once it arrives
  let stopped = false;

  function buildRows() {
    list.innerHTML = steps.map((s, i) => `
      <div class="agf-step" data-row="${i}" style="opacity:.35">
        <span class="agf-mark waiting"></span>
        <span class="agf-label" style="color:var(--muted);font-weight:500">${s}</span>
      </div>`).join("");
    rows = [...list.querySelectorAll("[data-row]")];
    paint();
  }

  function paint() {
    rows.forEach((row, i) => {
      const state = i < ticked ? "done" : i === ticked ? "active" : "waiting";
      const mark = row.querySelector(".agf-mark");
      const label = row.querySelector(".agf-label");
      const wanted = state === "done" ? CHECK : "";
      if (mark.className !== `agf-mark ${state}`) {
        mark.className = `agf-mark ${state}`;
        mark.innerHTML = wanted;
        if (state !== "done") mark.style.borderTopColor = state === "active" ? "var(--violet)" : "";
      }
      row.style.opacity = state === "waiting" ? "0.35" : "1";
      label.style.color = state === "done" ? "var(--green)" : "var(--ink)";
      label.style.fontWeight = state === "active" ? "700" : "500";
    });
  }

  /* one tick per TICK_MS, but only as far as the server has actually got */
  const advance = () => {
    if (stopped) return;
    if (!container.isConnected) { stopped = true; return; } // navigated away — never auto-open the report from a dead screen
    const goal = settledScan ? steps.length : serverDone;
    if (ticked < goal) { ticked += 1; paint(); }
    if (settledScan && ticked >= steps.length) {
      stopped = true;
      setTimeout(() => onCompleted(settledScan), FINISH_MS);
      return;
    }
    setTimeout(advance, TICK_MS);
  };

  function showError(message) {
    stopped = true;
    errBox.style.display = "block";
    errBox.innerHTML = message;
    onFailed();
  }

  const poll = async () => {
    if (stopped) return;
    /* The user may have navigated away (cancel, browser back). Once this screen's DOM
       is detached, the poll must die — it used to keep running and yank the user into
       the report the moment the scan finished. */
    if (!container.isConnected) { stopped = true; return; }
    let scan;
    try {
      scan = await api.scan(scanId);
    } catch (err) {
      showError(err.message);
      return;
    }
    if (scan.status === "failed") { showError(scan.error || "The scan failed"); return; }

    const p = scan.progress;
    if (p && Array.isArray(p.steps) && p.steps.length && p.steps.join("|") !== steps.join("|")) {
      steps = p.steps.slice();
      buildRows();
    }
    /* the server's label can carry news the step list cannot, e.g. a SERP check that
       failed and degraded the run to Search Console data only */
    if (p && p.label && !steps.includes(p.label)) {
      note.style.display = "block";
      note.textContent = p.label;
    }
    serverDone = scan.status === "completed" ? steps.length : Math.max(0, (p && p.step ? p.step : 1) - 1);
    if (scan.status === "completed") { settledScan = scan; return; }
    setTimeout(poll, 1200);
  };

  buildRows();
  setTimeout(advance, TICK_MS);
  poll();
}
